import { useState, useEffect, useCallback } from 'react';
import { FiClock, FiCalendar, FiUser, FiExternalLink, FiInfo, FiStar, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import { FaWhatsapp, FaCheckCircle, FaExclamationTriangle, FaRedo, FaSpinner, FaChalkboardTeacher } from 'react-icons/fa';
import { RiMedalLine } from 'react-icons/ri';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import StarRating from '../../Components/StarRating';
import Avatar from '../../Components/Avatar';
import { useNavigate } from 'react-router-dom';
import { formatDateLocale } from '../../utils/dateFormatter';
import { formatName } from '../../utils/dataTransformers';

function MyClasses() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('list');
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewingTrainer, setViewingTrainer] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [expandedClass, setExpandedClass] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState('attended');
  const [isLoading, setIsLoading] = useState(true);
  const [enrolledClasses, setEnrolledClasses] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const [isPWA, setIsPWA] = useState(false);

  // Fetch enrolled classes from backend
  const fetchClasses = useCallback(async (isRetry = false) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await HttpInterceptor.get('/api/students/classes');
      if (response.status === 200) {
        // Handle both array and object responses
        const classesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        setEnrolledClasses(classesData);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load classes';
      
      if (retryCount < MAX_RETRIES && !isRetry) {
        setError({ 
          message: errorMessage, 
          retryable: true 
        });
        toast.error('Failed to load classes. Please try again.');
      } else {
        setError({ 
          message: 'Unable to load classes. Please check your connection and try again later.', 
          retryable: false 
        });
        toast.error('Unable to load classes');
      }
      setEnrolledClasses([]);
    } finally {
      setIsLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchClasses(true);
  };

  // Update student progress via API
  const updateStudentProgress = async (planId, progressData) => {
    try {
      const response = await HttpInterceptor.patch(`/api/students/progress/${planId}`, progressData);
      if (response.status === 200) {
        toast.success('Progress updated successfully');
        return response.data;
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error(error.response?.data?.message || 'Failed to update progress');
      throw error;
    }
  };

  const markAttendance = async (sessionId, studentId, attended) => {
    try {
      const response = await HttpInterceptor.post('/api/students/attendance', {
        classId: sessionId,
        attended: attended
      });
      
      if (response.status === 200) {
        toast.success('Attendance marked successfully');
        // Update local state if needed
        return response.data;
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast.error('Failed to mark attendance');
      throw error;
    }
  };

  const handleMarkAttendance = async () => {
    if (!currentSession) return;

    try {
      await markAttendance(currentSession.id, 'current-student-id', attendanceStatus === 'attended');
      setShowAttendanceModal(false);
      setCurrentSession(null);
      setAttendanceStatus('attended');
    } catch (error) {
      console.error('Error marking attendance:', error);
    }
  };

  const toggleExpand = (classId, e) => {
    e.stopPropagation();
    setExpandedClass(expandedClass === classId ? null : classId);
  };

  const viewTrainerProfile = (trainer, e) => {
    e.stopPropagation();
    setViewingTrainer(trainer);
  };

  const closeTrainerProfile = () => {
    setViewingTrainer(null);
  };

  const changeMonth = (increment) => {
    const newMonth = currentMonth + increment;
    if (newMonth < 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else if (newMonth > 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(newMonth);
    }
  };

  const renderStars = (rating) => {
    return <StarRating rating={rating || 0} size="sm" />;
  };

  const getClassesForDay = (dayOfWeek) => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return enrolledClasses.filter(classItem => 
      classItem.days.includes(dayNames.indexOf(dayOfWeek))
    );
  };

  // Get all occurrences of a class for a given month
  const getClassOccurrencesForMonth = (classItem, month, year) => {
    const occurrences = [];
    const nextOccurrence = getNextOccurrenceDate(classItem);
    if (!nextOccurrence) return occurrences;

    // Get the first day of the month
    const firstDayOfMonth = new Date(year, month, 1);
    firstDayOfMonth.setHours(0, 0, 0, 0);
    
    // Get the last day of the month
    const lastDayOfMonth = new Date(year, month + 1, 0);
    lastDayOfMonth.setHours(23, 59, 59, 999);

    // Start from the next occurrence or the first day of month, whichever is later
    let currentDate = new Date(nextOccurrence);
    if (currentDate < firstDayOfMonth) {
      // If next occurrence is before the month, find the first occurrence in this month
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      let dayIndex;
      if (typeof classItem.day === 'string') {
        dayIndex = dayNames.findIndex(d => d.toLowerCase() === classItem.day.toLowerCase());
        if (dayIndex === -1) dayIndex = parseInt(classItem.day);
      } else {
        dayIndex = classItem.day;
      }
      
      // Find first occurrence of this day in the month
      const firstDay = new Date(year, month, 1);
      const firstDayOfWeek = firstDay.getDay();
      let daysToAdd = (dayIndex - firstDayOfWeek + 7) % 7;
      currentDate = new Date(year, month, 1 + daysToAdd);
      
      // Parse time
      const timeStr = classItem.time.trim();
      let hours, minutes;
      if (timeStr.includes('AM') || timeStr.includes('PM')) {
        const [timePart, period] = timeStr.split(/\s*(AM|PM)/i);
        const [h, m] = timePart.split(':').map(Number);
        hours = period.toUpperCase() === 'PM' && h !== 12 ? h + 12 : (period.toUpperCase() === 'AM' && h === 12 ? 0 : h);
        minutes = m || 0;
      } else {
        const parts = timeStr.split(':');
        hours = parseInt(parts[0]) || 0;
        minutes = parseInt(parts[1]) || 0;
      }
      currentDate.setHours(hours, minutes, 0, 0);
      
      // Check if program effective date is later
      if (classItem.programEffectiveDate) {
        const effectiveDate = new Date(classItem.programEffectiveDate);
        effectiveDate.setHours(0, 0, 0, 0);
        if (effectiveDate > currentDate) {
          // Find first occurrence after effective date
          const effectiveDay = effectiveDate.getDay();
          let daysToAdd = (dayIndex - effectiveDay + 7) % 7;
          currentDate = new Date(effectiveDate);
          currentDate.setDate(effectiveDate.getDate() + daysToAdd);
          currentDate.setHours(hours, minutes, 0, 0);
        }
      }
    }

    // Get program end date and last access date
    const programEndDate = classItem.programEndDate ? new Date(classItem.programEndDate) : null;
    const lastAccessDate = classItem.lastAccessDate ? new Date(classItem.lastAccessDate) : null;
    
    // Determine the cutoff date (earliest of program end date or last access date)
    let cutoffDate = null;
    if (programEndDate && lastAccessDate) {
      cutoffDate = programEndDate < lastAccessDate ? programEndDate : lastAccessDate;
    } else if (programEndDate) {
      cutoffDate = programEndDate;
    } else if (lastAccessDate) {
      cutoffDate = lastAccessDate;
    }

    // Generate all occurrences in this month, but only up to the cutoff date
    while (currentDate <= lastDayOfMonth) {
      if (currentDate >= firstDayOfMonth) {
        // Check if this occurrence is within the valid period
        if (!cutoffDate || currentDate <= cutoffDate) {
          occurrences.push(new Date(currentDate));
        } else {
          // If we've passed the cutoff date, stop generating occurrences
          break;
        }
      }
      // Move to next week
      currentDate.setDate(currentDate.getDate() + 7);
    }

    return occurrences;
  };

  const getClassesForDate = (day, month, year) => {
    const targetDate = new Date(year, month, day);
    targetDate.setHours(0, 0, 0, 0);
    const targetDateEnd = new Date(targetDate);
    targetDateEnd.setHours(23, 59, 59, 999);

    return enrolledClasses.filter(classItem => {
      // Check if target date is within program validity and payment access period
      const programEndDate = classItem.programEndDate ? new Date(classItem.programEndDate) : null;
      const lastAccessDate = classItem.lastAccessDate ? new Date(classItem.lastAccessDate) : null;
      
      // Check program end date
      if (programEndDate && targetDate > programEndDate) {
        return false; // Beyond program validity
      }
      
      // Check last access date (based on payments)
      if (lastAccessDate && targetDate > lastAccessDate) {
        return false; // Beyond paid period
      }
      
      const occurrences = getClassOccurrencesForMonth(classItem, month, year);
      return occurrences.some(occ => {
        const occDate = new Date(occ);
        occDate.setHours(0, 0, 0, 0);
        return occDate.getTime() === targetDate.getTime();
      });
    });
  };

  const hasUpcomingClasses = (day, month, year) => {
    const classes = getClassesForDate(day, month, year);
    const targetDate = new Date(year, month, day);
    targetDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return classes.length > 0 && targetDate >= now;
  };

  // Calculate next occurrence date for a class
  const getNextOccurrenceDate = (classItem) => {
    if (!classItem.day || !classItem.time) return null;

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Handle day as string (e.g., "Friday") or number (0-6)
    let dayIndex;
    if (typeof classItem.day === 'string') {
      dayIndex = dayNames.findIndex(d => d.toLowerCase() === classItem.day.toLowerCase());
      if (dayIndex === -1) {
        // Try to parse as number string
        dayIndex = parseInt(classItem.day);
        if (isNaN(dayIndex) || dayIndex < 0 || dayIndex > 6) return null;
      }
    } else {
      dayIndex = classItem.day;
      if (dayIndex < 0 || dayIndex > 6) return null;
    }

    // Get program effective date (start date) if available
    let startDate = new Date();
    if (classItem.programEffectiveDate) {
      const effectiveDate = new Date(classItem.programEffectiveDate);
      effectiveDate.setHours(0, 0, 0, 0);
      // Use the later of today or program start date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      startDate = effectiveDate > today ? effectiveDate : today;
    }

    const now = new Date();
    const startDay = startDate.getDay();
    
    // Parse time (format: "05:45" or "05:45 AM")
    let hours, minutes;
    const timeStr = classItem.time.trim();
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      // 12-hour format
      const [timePart, period] = timeStr.split(/\s*(AM|PM)/i);
      const [h, m] = timePart.split(':').map(Number);
      hours = period.toUpperCase() === 'PM' && h !== 12 ? h + 12 : (period.toUpperCase() === 'AM' && h === 12 ? 0 : h);
      minutes = m || 0;
    } else {
      // 24-hour format
      const parts = timeStr.split(':');
      hours = parseInt(parts[0]) || 0;
      minutes = parseInt(parts[1]) || 0;
    }

    // Calculate days until next occurrence from start date
    const daysUntilNext = (dayIndex - startDay + 7) % 7;
    
    // If start date is the class day, check if the time has passed
    let nextDate = new Date(startDate);
    if (daysUntilNext === 0) {
      // Same day - check if time has passed
      const classTime = new Date(startDate);
      classTime.setHours(hours, minutes, 0, 0);
      
      // If class time hasn't passed on start date, use start date; otherwise use next week
      if (now < classTime) {
        return classTime;
      } else {
        nextDate.setDate(startDate.getDate() + 7);
      }
    } else {
      nextDate.setDate(startDate.getDate() + daysUntilNext);
    }

    // Set the time
    nextDate.setHours(hours, minutes, 0, 0);

    // Ensure the date is not in the past
    if (nextDate < now) {
      // If calculated date is in the past, move to next week
      nextDate.setDate(nextDate.getDate() + 7);
    }

    return nextDate;
  };

  const TrainerProfileModal = ({ trainer, onClose }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-semibold">Trainer Profile</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FiX className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex items-center mb-4">
            <Avatar
              src={trainer.image || trainer.profileImage}
              name={trainer.name || 'Trainer'}
              size={64}
              alt={formatName(trainer.name || 'Trainer')}
              className="mr-4"
            />
            <div>
              <h3 className="text-lg font-semibold">{formatName(trainer.name || 'Trainer')}</h3>
              <div className="flex items-center">
                {renderStars(trainer.rating)}
                <span className="ml-2 text-sm text-gray-600">{trainer.rating}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <h4 className="font-medium text-gray-900">Experience</h4>
              <p className="text-sm text-gray-600">{trainer.yearsExperience} years</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900">Certifications</h4>
              <div className="flex flex-wrap gap-1 mt-1">
                {(trainer.certifications || []).map((cert, index) => (
                  <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {cert}
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900">Bio</h4>
              <p className="text-sm text-gray-600">{trainer.bio}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AttendanceModal = ({ session, onClose }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-semibold">Mark Attendance</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FiX className="w-6 h-6" />
            </button>
          </div>
          
          <div className="mb-4">
            <h3 className="font-medium mb-2">{session?.name}</h3>
            <p className="text-sm text-gray-600">{session?.schedule}</p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attendance Status
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="attendance"
                  value="attended"
                  checked={attendanceStatus === 'attended'}
                  onChange={(e) => setAttendanceStatus(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Attended</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="attendance"
                  value="missed"
                  checked={attendanceStatus === 'missed'}
                  onChange={(e) => setAttendanceStatus(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Missed</span>
              </label>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleMarkAttendance}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Mark Attendance
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CalendarView = () => {
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return (
      <div className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-200/50 shadow-xl' : 'bg-white rounded-lg shadow-sm'} rounded-lg p-6`}>
        <div className={`flex ${isPWA ? 'flex-col sm:flex-row' : ''} justify-between items-center ${isPWA ? 'mb-4 sm:mb-4' : 'mb-4'} ${isPWA ? 'gap-3' : ''}`}>
          <h2 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-xl'} font-semibold ${isPWA ? 'text-teal-800' : ''}`}>Calendar View</h2>
          <div className={`flex items-center ${isPWA ? 'gap-2' : 'space-x-2'}`}>
            <button
              onClick={() => changeMonth(-1)}
              className={`p-2 ${isPWA ? 'bg-teal-50 hover:bg-teal-100 border border-teal-200/50 text-teal-700 shadow-sm' : 'hover:bg-gray-100'} rounded transition-all ${isPWA ? 'font-semibold' : ''}`}
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            <span className={`font-medium ${isPWA ? 'text-teal-800' : ''}`}>
              {formatDateLocale(new Date(currentYear, currentMonth).toISOString(), null, { 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            <button
              onClick={() => changeMonth(1)}
              className={`p-2 ${isPWA ? 'bg-teal-50 hover:bg-teal-100 border border-teal-200/50 text-teal-700 shadow-sm' : 'hover:bg-gray-100'} rounded transition-all ${isPWA ? 'font-semibold' : ''}`}
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
          
          {days.map((day, index) => {
            const dayClasses = day ? getClassesForDate(day, currentMonth, currentYear) : [];
            const hasClasses = dayClasses.length > 0;
            const isToday = day && new Date().getDate() === day && 
                           new Date().getMonth() === currentMonth && 
                           new Date().getFullYear() === currentYear;
            
            // Count paid and unpaid classes
            const paidClasses = dayClasses.filter(c => c.hasAccess !== false);
            const unpaidClasses = dayClasses.filter(c => c.hasAccess === false);
            const totalClasses = dayClasses.length;
            
            return (
              <div
                key={index}
                className={`relative p-2 min-h-[100px] ${isPWA ? 'border-2' : 'border'} ${isPWA ? 'border-teal-200/50' : 'border-gray-200'} ${
                  day ? (isPWA ? 'cursor-pointer hover:bg-teal-50/70 hover:border-teal-300/70' : 'cursor-pointer hover:bg-gray-50') : (isPWA ? 'bg-teal-50/30' : 'bg-gray-100')
                } transition-all ${isToday ? (isPWA ? 'ring-2 ring-teal-400 bg-teal-50/30' : 'ring-2 ring-blue-400 bg-blue-50/30') : ''}`}
                onClick={() => {
                  if (day) {
                    if (unpaidClasses.length > 0 && unpaidClasses[0].paymentOverdueMessage) {
                      toast.error(unpaidClasses[0].paymentOverdueMessage, {
                        position: "top-center",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                      });
                    }
                    setSelectedDate({ day, month: currentMonth, year: currentYear });
                  }
                }}
              >
                {day && (
                  <>
                    <div className="flex items-start justify-between mb-1">
                      <div className={`text-sm font-medium ${isPWA ? 'text-teal-800' : 'text-gray-700'} ${isToday ? 'font-bold' : ''}`}>
                        {day}
                      </div>
                      {hasClasses && (
                        <div className="flex items-center gap-1 flex-wrap justify-end">
                          {/* Paid classes indicator */}
                          {paidClasses.length > 0 && (
                            <div 
                              className={`w-2 h-2 rounded-full ${isPWA ? 'bg-teal-500' : 'bg-blue-500'} shadow-sm`}
                              title={`${paidClasses.length} paid ${paidClasses.length === 1 ? 'class' : 'classes'}`}
                            />
                          )}
                          {/* Unpaid classes indicator */}
                          {unpaidClasses.length > 0 && (
                            <div 
                              className="w-2 h-2 rounded-full bg-gray-400 opacity-70 shadow-sm"
                              title={`${unpaidClasses.length} unpaid ${unpaidClasses.length === 1 ? 'class' : 'classes'}`}
                            />
                          )}
                          {/* Class count badge if multiple classes */}
                          {totalClasses > 1 && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              unpaidClasses.length > 0 
                                ? 'bg-gray-200 text-gray-600' 
                                : (isPWA ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700')
                            }`}>
                              {totalClasses}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Show first class time as a subtle indicator */}
                    {hasClasses && dayClasses[0] && (
                      <div className="mt-1">
                        <div className={`text-[10px] ${isPWA ? 'text-teal-600' : 'text-blue-600'} font-medium truncate`}>
                          {dayClasses[0].time}
                        </div>
                        {totalClasses > 1 && (
                          <div className={`text-[9px] ${isPWA ? 'text-teal-500' : 'text-blue-500'} mt-0.5`}>
                            +{totalClasses - 1} more
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const DateDetailModal = ({ day, month, year, classes, onClose, toggleExpand, expandedClass, viewTrainerProfile }) => {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {formatDateLocale(new Date(year, month, day).toISOString(), null, { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h2>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-6">
            {classes.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No classes scheduled for this day</p>
            ) : (
              <div className="space-y-4">
                {classes.map(classItem => {
                  const isUnpaid = classItem.hasAccess === false;
                  return (
                  <div 
                    key={classItem.id} 
                    onClick={() => {
                      if (isUnpaid && classItem.paymentOverdueMessage) {
                        toast.error(classItem.paymentOverdueMessage, {
                          position: "top-center",
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                        });
                      }
                    }}
                    className={`border rounded-lg p-4 transition-shadow ${
                      isUnpaid 
                        ? 'border-gray-300 bg-gray-50 opacity-70 cursor-pointer hover:bg-gray-100' 
                        : 'border-gray-200 hover:shadow-md'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className={`font-semibold text-lg ${isUnpaid ? 'text-gray-500' : ''}`}>{classItem.name}</h3>
                        <p className={`text-sm mt-1 ${isUnpaid ? 'text-gray-400' : 'text-gray-600'}`}>{classItem.time}</p>
                        {isUnpaid && classItem.paymentOverdueMessage && (
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                            <span className="font-medium">⚠️ Payment Required</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(classItem.id, e);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium ml-4"
                      >
                        {expandedClass === classItem.id ? 'Less' : 'More'}
                      </button>
                    </div>
                    
                    {expandedClass === classItem.id && (
                      <div className="mt-4 space-y-3 pt-4 border-t border-gray-100">
                        <p className={`text-sm ${isUnpaid ? 'text-gray-400' : 'text-gray-600'}`}>{classItem.description}</p>
                        <div className={`flex items-center text-sm ${isUnpaid ? 'text-gray-400' : 'text-gray-600'}`}>
                          <FiUser className="mr-2" />
                          <span>{classItem.instructor.name}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              viewTrainerProfile(classItem.instructor, e);
                              onClose();
                            }}
                            className="ml-2 text-blue-600 hover:underline"
                          >
                            View Profile
                          </button>
                        </div>
                        <div className={`flex items-center text-sm ${isUnpaid ? 'text-gray-400' : 'text-gray-600'}`}>
                          <FiClock className="mr-2" />
                          <span>{classItem.duration}</span>
                        </div>
                        <div className={`flex items-center text-sm ${isUnpaid ? 'text-gray-400' : 'text-gray-600'}`}>
                          <FiInfo className="mr-2" />
                          <span>Level: {classItem.level}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white' : 'bg-gray-50'}`}>
      {/* Hero Section */}
      <div className={`relative ${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : 'bg-gradient-to-r from-blue-600 to-purple-700'} ${isPWA ? 'text-gray-900' : 'text-white'}`}>
        {isPWA && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
        )}
        {!isPWA && (
          <div className="absolute inset-0 bg-black opacity-20"></div>
        )}
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-teal-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'}`}>
                My Classes
              </h1>
              <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-xl opacity-90'}`}>
                Manage your enrolled classes and track attendance
              </p>
            </div>
            {!isPWA && (
              <div className="hidden md:block">
                <img src="/src/images/yg1.jpg" alt="Yoga" className="w-32 h-32 rounded-full object-cover" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-b-2 border-teal-200/50 shadow-lg' : 'bg-white shadow-sm'}`}>
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-4 sm:py-6' : 'py-6'}`}>
          <div className={`flex ${isPWA ? 'flex-col sm:flex-row' : ''} justify-between items-${isPWA ? 'start sm:center' : 'center'} ${isPWA ? 'gap-4' : ''}`}>
            <div>
              <h2 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-2xl'} font-bold ${isPWA ? 'text-teal-800' : 'text-gray-900'}`}>
                Class Management
              </h2>
              <p className={`${isPWA ? 'mt-1 text-sm sm:text-base text-teal-600' : 'mt-2 text-gray-600'}`}>
                Switch between different views to manage your classes
              </p>
            </div>
            <div className={`flex ${isPWA ? 'gap-2' : 'space-x-2'}`}>
              <button
                onClick={() => {
                  setViewMode('list');
                  setSelectedDate(null); // Clear selected date when switching views
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'list' 
                    ? (isPWA ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg ring-2 ring-teal-300/50 font-semibold' : 'bg-blue-600 text-white')
                    : (isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-200/50 text-teal-700 hover:bg-teal-50 shadow-md font-semibold' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
                }`}
              >
                List View
              </button>
              <button
                onClick={() => {
                  setViewMode('calendar');
                  setSelectedDate(null); // Clear selected date when switching views
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'calendar' 
                    ? (isPWA ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg ring-2 ring-teal-300/50 font-semibold' : 'bg-blue-600 text-white')
                    : (isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-200/50 text-teal-700 hover:bg-teal-50 shadow-md font-semibold' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
                }`}
              >
                Calendar View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {isLoading && (
          <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'}`}>
            <FaSpinner className={`${isPWA ? 'text-5xl sm:text-6xl' : 'text-6xl'} mx-auto ${isPWA ? 'text-teal-500' : 'text-blue-600'} animate-spin mb-4`} />
            <p className={isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-gray-600'}>Loading your classes...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'}`}>
            <div className={`${isPWA ? 'text-red-400' : 'text-red-400'} mb-4`}>
              <FaExclamationTriangle className={`${isPWA ? 'text-5xl sm:text-6xl' : 'text-6xl'} mx-auto`} />
            </div>
            <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-medium ${isPWA ? 'text-teal-800' : 'text-gray-900'} mb-2`}>
              Unable to Load Classes
            </h3>
            <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-gray-500'} mb-6`}>{error.message}</p>
            {error.retryable && (
              <div className={`flex justify-center ${isPWA ? 'gap-3' : 'gap-4'} ${isPWA ? 'flex-wrap' : ''}`}>
                <button 
                  onClick={handleRetry}
                  disabled={isLoading}
                  className={`${isPWA ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg ring-2 ring-teal-300/50 hover:from-teal-600 hover:to-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'} px-6 py-3 rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isPWA ? 'font-semibold' : ''}`}
                >
                  <FaRedo className={isLoading ? 'animate-spin' : ''} />
                  Retry
                </button>
                <button 
                  onClick={() => navigate('/student/services')}
                  className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-300/50 text-teal-700 hover:bg-teal-50 shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-3 rounded-lg transition-colors ${isPWA ? 'font-semibold' : ''}`}
                >
                  Browse Classes
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!error && !isLoading && enrolledClasses.length === 0 && (
          <div className={`text-center ${isPWA ? 'py-8 sm:py-10' : 'py-12'}`}>
            <div className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-200/50 shadow-xl rounded-xl p-8' : ''}`}>
              <div className={`${isPWA ? 'text-teal-400' : 'text-gray-400'} mb-4`}>
                <FaChalkboardTeacher className={`${isPWA ? 'text-5xl sm:text-6xl' : 'text-6xl'} mx-auto`} />
              </div>
              <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-medium ${isPWA ? 'text-teal-800' : 'text-gray-900'} mb-2`}>
                No Classes Enrolled
              </h3>
              <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-gray-500'} mb-6`}>
                You haven't enrolled in any classes yet. Browse available classes to get started.
              </p>
              <button 
                onClick={() => navigate('/student/services')}
                className={`${isPWA ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg ring-2 ring-teal-300/50 hover:from-teal-600 hover:to-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'} px-6 py-3 rounded-lg transition-all ${isPWA ? 'font-semibold' : ''}`}
              >
                Browse Classes
              </button>
            </div>
          </div>
        )}

        {/* Classes Content */}
        {!error && !isLoading && enrolledClasses.length > 0 && (
        <>
        {viewMode === 'list' ? (
          <div className={`${isPWA ? 'space-y-4' : 'space-y-6'}`}>
            {[...enrolledClasses]
              .sort((a, b) => {
                const dateA = getNextOccurrenceDate(a);
                const dateB = getNextOccurrenceDate(b);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1; // Put items without dates at the end
                if (!dateB) return -1; // Put items without dates at the end
                return dateA.getTime() - dateB.getTime(); // Sort by date ascending
              })
              .map(classItem => {
                const isUnpaid = classItem.hasAccess === false;
                return (
              <div 
                key={classItem.id} 
                onClick={() => {
                  if (isUnpaid && classItem.paymentOverdueMessage) {
                    toast.error(classItem.paymentOverdueMessage, {
                      position: "top-center",
                      autoClose: 5000,
                      hideProgressBar: false,
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                    });
                  }
                }}
                className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-200/50 shadow-xl hover:shadow-2xl hover:border-teal-300/70' : 'bg-white rounded-lg shadow-sm'} rounded-lg p-6 transition-all ${
                  isUnpaid 
                    ? 'opacity-60 bg-gray-100 border-gray-300 cursor-pointer hover:bg-gray-200' 
                    : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={`text-xl font-semibold ${isUnpaid ? 'text-gray-500' : ''}`}>{classItem.name}</h3>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">{classItem.level}</span>
                        <button
                          onClick={(e) => toggleExpand(classItem.id, e)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          {expandedClass === classItem.id ? 'Less' : 'More'}
                        </button>
                      </div>
                    </div>
                    
                    <p className={`mb-3 ${isUnpaid ? 'text-gray-400' : 'text-gray-600'}`}>{classItem.description}</p>
                    
                    {isUnpaid && (
                      <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                        <span className="font-medium">⚠️ Payment Required</span> - Click to see details
                      </div>
                    )}
                    
                    <div className={`flex items-center space-x-6 text-sm ${isUnpaid ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className="flex items-center flex-wrap gap-2">
                        <div className="flex items-center">
                          <FiCalendar className="mr-1" />
                          <span>{classItem.schedule}</span>
                        </div>
                        {(() => {
                          const nextDate = getNextOccurrenceDate(classItem);
                          if (nextDate) {
                            return (
                              <span className={`${isPWA ? 'font-semibold text-teal-600' : 'font-medium text-blue-600'}`}>
                                • {formatDateLocale(nextDate.toISOString(), null, { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: nextDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                                })}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <div className="flex items-center">
                        <FiClock className="mr-1" />
                        <span>{classItem.duration}</span>
                      </div>
                      <div className="flex items-center">
                        <FiUser className="mr-1" />
                        <span>{classItem.instructor.name}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {expandedClass === classItem.id && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Instructor Details</h4>
                        <div className="flex items-center mb-3">
                          <Avatar
                            src={classItem.instructor.image || classItem.instructor.profileImage}
                            name={classItem.instructor.name || 'Instructor'}
                            size={48}
                            alt={classItem.instructor.name || 'Instructor'}
                            className="mr-3"
                          />
                          <div>
                            <p className="font-medium">{classItem.instructor.name || 'Instructor'}</p>
                            <div className="flex items-center">
                              {renderStars(classItem.instructor.rating)}
                              <span className="ml-1 text-sm text-gray-600">
                                {classItem.instructor.rating}
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{classItem.instructor.bio}</p>
                        <div className="flex flex-wrap gap-1">
                          {(classItem.instructor.certifications || []).map((cert, index) => (
                            <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Required Materials</h4>
                        {classItem.materials && classItem.materials.length > 0 ? (
                          <ul className="space-y-2">
                            {classItem.materials.map((material, index) => (
                              <li key={index} className="flex items-center text-sm text-gray-600">
                                <FiInfo className="mr-2 text-blue-500" />
                                {material}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500">No materials required</p>
                        )}
                        
                        <div className="mt-4">
                          <h4 className={`font-medium ${isPWA ? 'text-teal-800' : 'text-gray-900'} mb-3`}>Quick Actions</h4>
                          <div className={`flex ${isPWA ? 'flex-col gap-2' : 'space-x-2'} flex-wrap ${isPWA ? '' : 'gap-2'}`}>
                            {classItem.meetingLink ? (
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await HttpInterceptor.post(`/api/classes/${classItem._id}/join-meeting`, {
                                      sessionDate: new Date().toISOString().split('T')[0]
                                    });
                                    if (response.status === 200 && response.data) {
                                      if (response.data.attendanceMarked) {
                                        toast.success(response.data.message || 'Attendance marked! Opening class...');
                                      } else {
                                        toast.info(response.data.message || 'Activity logged. Opening class...');
                                      }
                                      // Open Meet link in new tab
                                      if (response.data.meetingLink) {
                                        window.open(response.data.meetingLink, '_blank', 'noopener,noreferrer');
                                      } else if (classItem.meetingLink) {
                                        window.open(classItem.meetingLink, '_blank', 'noopener,noreferrer');
                                      }
                                    }
                                  } catch (error) {
                                    console.error('Error joining meeting:', error);
                                    const errorMessage = error.response?.data?.message || 'Failed to join class';
                                    toast.error(errorMessage);
                                    // Still try to open the link if it exists
                                    if (classItem.meetingLink && error.response?.status !== 403) {
                                      window.open(classItem.meetingLink, '_blank', 'noopener,noreferrer');
                                    }
                                  }
                                }}
                                className={`flex items-center justify-center px-3 py-2 ${isPWA ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg ring-2 ring-red-300/50 hover:from-red-600 hover:to-pink-600 font-semibold' : 'bg-red-600 text-white hover:bg-red-700'} rounded-lg text-sm transition-all`}
                              >
                                <FaChalkboardTeacher className="mr-1" />
                                Join Class
                              </button>
                            ) : (
                              <div className={`flex items-center justify-center px-3 py-2 ${isPWA ? 'bg-gray-200/80 border-2 border-gray-300/50 text-gray-500' : 'bg-gray-200 text-gray-500'} rounded-lg text-sm ${isPWA ? 'font-semibold' : ''}`}>
                                <FiInfo className="mr-1" />
                                No meeting link available
                              </div>
                            )}
                            {classItem.whatsappLink ? (
                              <a
                                href={classItem.whatsappLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center justify-center px-3 py-2 ${isPWA ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg ring-2 ring-green-300/50 hover:from-green-600 hover:to-emerald-600 font-semibold' : 'bg-green-600 text-white hover:bg-green-700'} rounded-lg text-sm transition-all`}
                              >
                                <FaWhatsapp className="mr-1" />
                                Join WhatsApp Group
                              </a>
                            ) : null}
                            <button
                              onClick={() => {
                                setCurrentSession(classItem);
                                setShowAttendanceModal(true);
                              }}
                              className={`flex items-center justify-center px-3 py-2 ${isPWA ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg ring-2 ring-teal-300/50 hover:from-teal-600 hover:to-blue-600 font-semibold' : 'bg-blue-600 text-white hover:bg-blue-700'} rounded-lg text-sm transition-all`}
                            >
                              <FaCheckCircle className="mr-1" />
                              Mark Attendance
                            </button>
                          </div>
                          {classItem.meetingLink && (
                            <p className="text-xs text-gray-500 mt-2">
                              Join available: 30 min before to 2 hours after class time
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Class Recordings */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-3">Class Recordings</h4>
                      {classItem.recordings && classItem.recordings.length > 0 ? (
                        <div className="space-y-2">
                          {classItem.recordings.map((recording, index) => (
                            <div key={recording._id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">{recording.title}</p>
                                <p className="text-xs text-gray-600">
                                  Uploaded: {formatDateLocale(recording.uploadedAt)}
                                </p>
                              </div>
                              <a
                                href={recording.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                              >
                                <FiExternalLink className="mr-1" />
                                Watch Recording
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No recordings available yet</p>
                      )}
                    </div>

                    {/* Class Level Display */}
                    {classItem.level && (
                      <div className="mt-4">
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full capitalize">
                          {classItem.level} Level
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
                );
              })}
          </div>
        ) : (
          <CalendarView />
        )}
        </>
        )}
      </div>

      {/* Modals */}
      {viewingTrainer && (
        <TrainerProfileModal 
          trainer={viewingTrainer} 
          onClose={closeTrainerProfile} 
        />
      )}
      
      {showAttendanceModal && currentSession && (
        <AttendanceModal 
          session={currentSession} 
          onClose={() => {
            setShowAttendanceModal(false);
            setCurrentSession(null);
          }} 
        />
      )}
      
      {selectedDate && viewMode === 'calendar' && (
        <DateDetailModal
          day={selectedDate.day}
          month={selectedDate.month}
          year={selectedDate.year}
          classes={getClassesForDate(selectedDate.day, selectedDate.month, selectedDate.year)}
          onClose={() => setSelectedDate(null)}
          toggleExpand={toggleExpand}
          expandedClass={expandedClass}
          viewTrainerProfile={viewTrainerProfile}
        />
      )}
    </div>
  );
}

export default MyClasses;